/**
 * Push Notifications Hook (Hub Frontend)
 *
 * Verwaltet:
 *   - Service Worker Registration fuer Push
 *   - Permission-Request
 *   - Subscription-Management (subscribe/unsubscribe)
 *   - Status-Abfrage
 */
import { useState, useEffect, useCallback } from "react";

interface PushState {
  supported: boolean;
  permission: NotificationPermission;
  subscribed: boolean;
  loading: boolean;
  enabled: boolean; // Server-seitig konfiguriert?
}

export function usePushNotifications() {
  const [state, setState] = useState<PushState>({
    supported: false,
    permission: "default",
    subscribed: false,
    loading: true,
    enabled: false,
  });

  // Initialen Status laden
  useEffect(() => {
    async function init() {
      const supported = "serviceWorker" in navigator && "PushManager" in window;
      if (!supported) {
        setState((s) => ({ ...s, supported: false, loading: false }));
        return;
      }

      const permission = Notification.permission;

      // VAPID Key vom Server holen
      let enabled = false;
      let vapidKey = "";
      try {
        const res = await fetch("/api/notifications/vapid-key");
        const data = await res.json();
        enabled = data.enabled;
        vapidKey = data.publicKey;
      } catch {
        // Server nicht erreichbar
      }

      // Pruefen ob bereits subscribed
      let subscribed = false;
      if (permission === "granted") {
        try {
          const reg = await navigator.serviceWorker.getRegistration("/push-sw.js");
          if (reg) {
            const sub = await reg.pushManager.getSubscription();
            subscribed = !!sub;
          }
        } catch {
          // Ignorieren
        }
      }

      setState({ supported, permission, subscribed, loading: false, enabled });
    }

    init();
  }, []);

  // Push-Berechtigung anfordern und subscriben
  const subscribe = useCallback(async (): Promise<boolean> => {
    setState((s) => ({ ...s, loading: true }));

    try {
      // Permission anfordern
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setState((s) => ({ ...s, permission, loading: false }));
        return false;
      }

      // VAPID Key holen
      const vapidRes = await fetch("/api/notifications/vapid-key");
      const { publicKey } = await vapidRes.json();
      if (!publicKey) {
        setState((s) => ({ ...s, loading: false }));
        return false;
      }

      // Service Worker registrieren
      const reg = await navigator.serviceWorker.register("/push-sw.js", { scope: "/" });
      await navigator.serviceWorker.ready;

      // Push Subscription erstellen
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToArrayBuffer(publicKey),
      });

      // An Server senden
      const subJson = sub.toJSON();
      const res = await fetch("/api/notifications/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          endpoint: subJson.endpoint,
          keys: subJson.keys,
        }),
      });

      if (!res.ok) throw new Error("Subscribe fehlgeschlagen");

      setState((s) => ({ ...s, permission: "granted", subscribed: true, loading: false }));
      return true;
    } catch (err) {
      console.error("[push] subscribe error:", err);
      setState((s) => ({ ...s, loading: false }));
      return false;
    }
  }, []);

  // Unsubscribe
  const unsubscribe = useCallback(async (): Promise<boolean> => {
    setState((s) => ({ ...s, loading: true }));

    try {
      const reg = await navigator.serviceWorker.getRegistration("/push-sw.js");
      if (reg) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          // Server informieren
          await fetch("/api/notifications/unsubscribe", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ endpoint: sub.endpoint }),
          });
          await sub.unsubscribe();
        }
      }

      setState((s) => ({ ...s, subscribed: false, loading: false }));
      return true;
    } catch (err) {
      console.error("[push] unsubscribe error:", err);
      setState((s) => ({ ...s, loading: false }));
      return false;
    }
  }, []);

  // Test-Notification senden
  const sendTest = useCallback(async (): Promise<boolean> => {
    try {
      const res = await fetch("/api/notifications/test", { method: "POST" });
      return res.ok;
    } catch {
      return false;
    }
  }, []);

  return { ...state, subscribe, unsubscribe, sendTest };
}

// Helper: Base64-URL zu ArrayBuffer (fuer applicationServerKey)
function urlBase64ToArrayBuffer(base64String: string): ArrayBuffer {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const buffer = new ArrayBuffer(rawData.length);
  const outputArray = new Uint8Array(buffer);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return buffer;
}
